import { LayoutProps } from 'components/atoms/Layout/interfaces'
import { InputBuildProps } from 'components/molecules/InputBuild/interfaces'
import { SelectBuildProps } from 'components/molecules/SelectBuild/interfaces'
import { ContractMode } from 'contexts/contract/interface'
import { LocationsDTO } from 'models'
import { UFconditional } from 'pages/Contracts/utils/disableUfFields'
import { FormContractKeys } from './types'

interface Schema {
    id: FormContractKeys
}

interface FormContractInterface {
    input?: InputBuildProps & Schema
    select?: SelectBuildProps & Schema
    layout: LayoutProps
}

const optionsTypeRestrictions = {
    BA: 'typeBA',
    MS: 'typeMS',
    MT: 'typeMS',
    default: 'type'
}

export const FormContractValue = (
    contractRef: string,
    location: LocationsDTO[],
    typeContract: ContractMode | undefined
): FormContractInterface[] => {
    const currentDate = new Date().toISOString().slice(0, 10)
    const isExistingAdditive =
        typeContract === 'ADDITIVE' || typeContract === 'EDITADDITIVE'
    const isExternalAdditive = typeContract === 'EXTERNAL_ADDITIVE'
    const isAdditive = isExistingAdditive || isExternalAdditive

    const data = [
        {
            input: {
                id: 'dataContrato',
                label: 'Data Contrato',
                placeholder: 'dd/mm/aaaa',
                type: 'date',
                required: true,
                max: currentDate
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 4 }
            }
        },
        {
            select: {
                id: 'ufContrato',
                label: 'UF do contrato',
                optionsType: 'ufContract',
                required: true,
                isDisabled: true
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 3 }
            }
        },
        {
            select: {
                id: 'municipioContrato',
                label: 'Município do Contrato',
                options: location?.map(({ municipio, codigoTomDetran }) => ({
                    text: municipio,
                    value:
                        typeContract !== 'CREATE' &&
                        typeContract !== 'EXTERNAL_ADDITIVE'
                            ? municipio
                            : `${String(codigoTomDetran)}|${municipio}`
                })),
                isSearching: true
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 5 }
            }
        },
        {
            input: {
                id: 'numeroContrato',
                label: 'Nº do contrato',
                inputMode: 'numeric',
                required: true
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 4 }
            }
        },
        {
            select: {
                id: 'tipoRestricao',
                label: 'Tipo de gravame',
                optionsType:
                    optionsTypeRestrictions[contractRef as 'BA' | 'MS'] ||
                    optionsTypeRestrictions.default,
                required: true
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 4 }
            }
        },
        {
            input: {
                id: 'quantidadeMeses',
                label: 'Quantidade de meses',
                inputMode: 'numeric',
                maxLength: 3,
                required: true,
                onKeyDown: (e: any) =>
                    ['e', 'E', '+', '-', '.', ',', '='].includes(e.key) &&
                    e.preventDefault()
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 4 }
            }
        },
        ...(isExistingAdditive
            ? [
                {
                    input: {
                        id: 'dataAditivo',
                        label: 'Data do aditivo',
                        placeholder: 'dd/mm/aaaa',
                        type: 'date',
                        required: true
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 4 }
                    }
                }
            ]
            : []),
        ...(isExistingAdditive
            ? [
                {
                    select: {
                        id: 'tipoAditivo',
                        label: 'Tipo de Aditivo',
                        optionsType: 'tipoAditivo',
                        required: false
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 4 }
                    }
                }
            ]
            : []),
        ...(isAdditive
            ? [
                {
                    input: {
                        id: 'numAditivoContratoOrigem',
                        label: 'Nº do aditivo',
                        inputMode: 'numeric',
                        required: true,
                        onKeyDown: (e: any) =>
                            ['e', 'E', '+', '-', '.', ',', '='].includes(
                                e.key
                            ) && e.preventDefault()
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 4 }
                    }
                }
            ]
            : []),
        ...(isExistingAdditive
            ? [
                {
                    input: {
                        id: 'numAditivoContrato',
                        label: 'Nº registro do aditivo',
                        required: true
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 4 }
                    }
                }
            ]
            : []),
        ...(isExternalAdditive
            ? [
                {
                    input: {
                        id: 'numeroRegistroContrato',
                        label: 'Nº registro do contrato no DETRAN (opcional)',
                        maxLength: 30
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 8 }
                    }
                }
            ]
            : []),
        ...(contractRef === 'RS'
            ? [
                {
                    select: {
                        id: 'opcaoCompra',
                        label: 'Opção de compra',
                        optionsType: 'booleanType',
                    },
                    layout: {
                        type: 'col',
                        layout: { base: 12, md: 4 }
                    }
                }
            ]
            : []),
    ]

    if (UFconditional.includes(contractRef)) {
        data.push({
            input: {
                id: 'numeroDudaPago',
                label: 'Numero do DUDA',
                type: 'number',
                maxLength: 10,
                onKeyDown: (e: any) =>
                    ['e', 'E', '+', '-', '.', ',', '='].includes(e.key) &&
                    e.preventDefault()
            },
            layout: {
                type: 'col',
                layout: { base: 12, md: 6 }
            }
        } as any)
    }

    return data as FormContractInterface[]
}
